import { fillPath, route } from "./routes.js";
import { z } from "zod";
//#region src/scim-tokens.ts
/**
* `/v1/admin/scim-tokens*` — the three SCIM-token routes the admin app's SCIM page calls.
*
* Producer: `AdminScimTokensController` in `cloud/dp/core-api/src/admin/`. Consumer: the admin
* app's `AdminClient`. The shell's read types differ from the controller in two places:
*
* 1. `POST` returns `{ id, token, description, expiresAt, createdAt }`. The shell's
*    `ScimTokenCreateResponse` declares three of those five and omits `expiresAt` and `createdAt`.
* 2. `revokeScimToken` is typed `Promise<void>` in the shell client. The controller returns
*    `{ message: 'SCIM token revoked' }` with status 200.
*
* Permission keys: the class default is `settings:identity:manage`, because minting a SCIM bearer
* opens a separate auth plane that no org permission bounds; the list handler overrides it to
* `settings:identity:read` because it returns ids, descriptions and expiries and never a raw or
* hashed token.
*
* `tokenHash` is on no response. The raw token is returned once, by the create route, and the
* server keeps only its SHA-256.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client. No shape in this file travels in both directions.
*/
/**
* One row of `GET /v1/admin/scim-tokens` — the four keys `listTokens` projects.
*
* `description` is caller-supplied free text and null when the create body omitted it. It is
* deliberately not recorded in the audit trail: the token id addresses the row that holds it.
* `expiresAt` is null for a token with no expiry.
*
* Owner: `AdminScimTokensController.listTokens`.
*/
const scimTokenInfoSchema = z.object({
	id: z.uuid(),
	description: z.string().nullable(),
	expiresAt: z.iso.datetime().nullable(),
	createdAt: z.iso.datetime()
});
/**
* `GET /v1/admin/scim-tokens` — the org's tokens, never a raw or hashed value. Requires
* `settings:identity:read`.
*/
const LIST_SCIM_TOKENS_ROUTE = route("GET", "/v1/admin/scim-tokens");
/**
* `POST /v1/admin/scim-tokens` — mint a token and return the raw value once. Status 201. Requires
* `settings:identity:manage`.
*/
const CREATE_SCIM_TOKEN_ROUTE = route("POST", "/v1/admin/scim-tokens");
/**
* `DELETE /v1/admin/scim-tokens/{id}` — revoke a token. Status 200; 404 both when the id does not
* exist and when it belongs to another org, so the response is no cross-tenant existence oracle.
* Requires `settings:identity:manage`.
*/
const REVOKE_SCIM_TOKEN_ROUTE = route("DELETE", "/v1/admin/scim-tokens/{id}");
/**
* Fill `/v1/admin/scim-tokens/{id}`. The placeholder is `id`, the name `@Param('id')` puts in
* `openapi.json`.
*/
function adminScimTokenPath(tokenId) {
	return fillPath(REVOKE_SCIM_TOKEN_ROUTE.path, { id: tokenId });
}
/** The body of `GET /v1/admin/scim-tokens`. A bare array. */
const listScimTokensResponseSchema = z.array(scimTokenInfoSchema);
/**
* The body of `POST /v1/admin/scim-tokens`.
*
* The key is `description`, not `name`. The shell sent `{ name }` and
* `ZionStrictValidationPipe` deep-stricts this schema, so generating a SCIM token from the shell
* was a hard 400 until #1137.
*
* `expiresInDays` is optional and unbounded on the producer, which stores `null` for an absent
* value — a token with no expiry. A zero or negative value is accepted by the schema and is falsy,
* so the service treats it as absent.
*
* Owner: `generateScimTokenSchema` in `admin-scim-tokens.controller.ts`.
*/
const createScimTokenRequestSchema = z.strictObject({
	description: z.string().optional(),
	expiresInDays: z.number().optional()
});
/**
* The body of `POST /v1/admin/scim-tokens` — status 201, five keys.
*
* `token` is the raw bearer, 48 random bytes base64url-encoded, and this is the only response that
* ever carries it. The caller must show it before it navigates away.
*
* Owner: `AdminScimTokensController.generateToken`.
*/
const createScimTokenResponseSchema = z.object({
	id: z.uuid(),
	token: z.string(),
	description: z.string().nullable(),
	expiresAt: z.iso.datetime().nullable(),
	createdAt: z.iso.datetime()
});
/**
* The body of `DELETE /v1/admin/scim-tokens/{id}` — status 200,
* `{ message: 'SCIM token revoked' }`. The shell client types it `Promise<void>`.
*/
const revokeScimTokenResponseSchema = z.object({ message: z.string() });
/** Every route this file names, for `route-census.ts`. */
const scimTokensRoutes = Object.freeze([
	LIST_SCIM_TOKENS_ROUTE,
	CREATE_SCIM_TOKEN_ROUTE,
	REVOKE_SCIM_TOKEN_ROUTE
]);
//#endregion
export { CREATE_SCIM_TOKEN_ROUTE, LIST_SCIM_TOKENS_ROUTE, REVOKE_SCIM_TOKEN_ROUTE, adminScimTokenPath, createScimTokenRequestSchema, createScimTokenResponseSchema, listScimTokensResponseSchema, revokeScimTokenResponseSchema, scimTokenInfoSchema, scimTokensRoutes };

//# sourceMappingURL=scim-tokens.js.map